import { supabase, isConfigured, storePendingMarketingConsent, checkRateLimits, logEvent } from '@rehub/supabase';
import { persistAiProvenance } from '@rehub/ai-transparency';
import {
  DEFAULT_HYPER_DUSK_ANIMATION_STYLE,
  DEFAULT_HYPER_DUSK_MOTION_PROMPT,
  DEFAULT_HYPER_DUSK_NANO_PROMPT,
  type HyperDuskAnimationStyle,
  type HyperDuskOutputMode,
  type HyperDuskProject,
  type HyperDuskProjectImage,
  type HyperDuskVariant,
} from '../types';

export { supabase, isConfigured, storePendingMarketingConsent, checkRateLimits, logEvent };

/**
 * Persist AI provenance for a dusk-ready Hyper Dusk image.
 * Fail-open: never throws; returns false on any error.
 */
export async function applyHyperDuskProvenance(
  imageId: string,
  opts: {
    sourceAssetId?: string | null;
    outputAssetId?: string | null;
    operation?: 'hyperdusk_dusk' | 'hyperdusk_animation';
  } = {},
): Promise<boolean> {
  try {
    const { ok } = await persistAiProvenance({
      supabase,
      target: 'imagemotion_project_images',
      id: imageId,
      app: 'hyperdusk',
      operation: opts.operation ?? 'hyperdusk_dusk',
      sourceAssetId: opts.sourceAssetId || undefined,
      outputAssetId: opts.outputAssetId || undefined,
      provider: 'google',
      model: 'gemini-3-pro-image-preview',
    });
    return ok;
  } catch (err) {
    console.warn('applyHyperDuskProvenance failed:', err);
    return false;
  }
}

// Stripe billing portal (invoices, payment methods, subscriptions). Shared
// across every ReHub app via @rehub/commerce + the single Supabase project.
import { openBillingPortal as openBillingPortalShared } from '@rehub/commerce';
export const openBillingPortal = (returnUrl?: string) =>
  openBillingPortalShared(supabase, returnUrl);

const STORAGE_BUCKET = 'imagemotion-uploads';
const STORAGE_RETRY_ATTEMPTS = 5;
const STORAGE_RETRY_DELAY_MS = 700;

function getErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) return error.message;
  if (error && typeof error === 'object') {
    const maybe = error as { message?: string; details?: string; hint?: string; code?: string };
    const parts = [maybe.message, maybe.details, maybe.hint].filter(Boolean);
    if (parts.length) return parts.join(' | ');
    if (maybe.code) return `${fallback} (${maybe.code})`;
  }
  return fallback;
}

async function getFunctionInvokeErrorMessage(error: unknown, fallback: string): Promise<string> {
  if (!error || typeof error !== 'object') {
    return getErrorMessage(error, fallback);
  }
  const maybe = error as { context?: Response };
  if (maybe.context && typeof maybe.context.json === 'function') {
    try {
      const payload = await maybe.context.clone().json() as { error?: string; message?: string; details?: string };
      const parts = [payload.error, payload.message, payload.details].filter(Boolean);
      if (parts.length) return parts.join(' | ');
    } catch {
      // no-op
    }
  }
  return getErrorMessage(error, fallback);
}

function normalizeDuskVariants(value: unknown): HyperDuskVariant[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => {
      if (!item || typeof item !== 'object') return null;
      const raw = item as Record<string, unknown>;
      if (typeof raw.id !== 'string' || typeof raw.path !== 'string') return null;
      return {
        id: raw.id,
        path: raw.path,
        url: typeof raw.url === 'string' ? raw.url : null,
        created_at: typeof raw.created_at === 'string' ? raw.created_at : new Date().toISOString(),
        preset_id: typeof raw.preset_id === 'string' ? raw.preset_id : null,
        animation_style: typeof raw.animation_style === 'string' ? raw.animation_style as HyperDuskAnimationStyle : null,
        nano_request_id: typeof raw.nano_request_id === 'string' ? raw.nano_request_id : null,
      } satisfies HyperDuskVariant;
    })
    .filter((v): v is HyperDuskVariant => !!v);
}

function extractStoragePath(signedUrl: string): string | null {
  try {
    const url = new URL(signedUrl);
    const marker = `/object/sign/${STORAGE_BUCKET}/`;
    const idx = url.pathname.indexOf(marker);
    if (idx === -1) return null;
    return decodeURIComponent(url.pathname.slice(idx + marker.length));
  } catch {
    return null;
  }
}

function isRetryableNetworkError(error: unknown): boolean {
  const message = getErrorMessage(error, '').toLowerCase();
  return (
    message.includes('failed to fetch') ||
    message.includes('networkerror') ||
    message.includes('network request failed') ||
    message.includes('timed out') ||
    message.includes('timeout')
  );
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export async function uploadToStorage(
  file: File,
  folder: 'images' | 'music' | 'logos',
  userId: string
): Promise<{ url: string; path: string }> {
  const ext = file.name.split('.').pop() || 'bin';
  const storagePath = `${folder}/${userId}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;

  let uploadedPath: string | null = null;
  let lastUploadError: unknown = null;

  for (let attempt = 1; attempt <= STORAGE_RETRY_ATTEMPTS; attempt++) {
    const { data, error } = await supabase.storage
      .from(STORAGE_BUCKET)
      .upload(storagePath, file, {
        cacheControl: '3600',
        upsert: false,
      });

    if (!error && data?.path) {
      uploadedPath = data.path;
      break;
    }

    lastUploadError = error ?? new Error('Unknown storage upload error');

    // Network failures can happen after the object is actually persisted.
    // Probe the exact target path before retrying to avoid false-negative uploads.
    if (isRetryableNetworkError(lastUploadError)) {
      try {
        const { data: probeSigned, error: probeError } = await supabase.storage
          .from(STORAGE_BUCKET)
          .createSignedUrl(storagePath, 60 * 10);
        if (!probeError && probeSigned?.signedUrl) {
          uploadedPath = storagePath;
          break;
        }
      } catch {
        // Keep retrying upload flow.
      }
    }

    if (attempt < STORAGE_RETRY_ATTEMPTS && isRetryableNetworkError(lastUploadError)) {
      await sleep(STORAGE_RETRY_DELAY_MS * attempt);
      continue;
    }
    throw lastUploadError;
  }

  if (!uploadedPath) {
    throw new Error(getErrorMessage(lastUploadError, 'Failed to upload file'));
  }

  let lastSignError: unknown = null;
  for (let attempt = 1; attempt <= STORAGE_RETRY_ATTEMPTS; attempt++) {
    const { data: signedData, error: signError } = await supabase.storage
      .from(STORAGE_BUCKET)
      .createSignedUrl(uploadedPath, 60 * 60 * 24 * 7);

    if (!signError && signedData?.signedUrl) {
      return { url: signedData.signedUrl, path: uploadedPath };
    }

    lastSignError = signError ?? new Error('Unknown signed URL error');
    if (attempt < STORAGE_RETRY_ATTEMPTS && isRetryableNetworkError(lastSignError)) {
      await sleep(STORAGE_RETRY_DELAY_MS * attempt);
      continue;
    }
    break;
  }

  // Fallback: if URL signing is flaky, keep the uploaded object path and use a
  // deterministic URL so project creation can continue. The app/worker can re-sign via path.
  const { data: publicData } = supabase.storage.from(STORAGE_BUCKET).getPublicUrl(uploadedPath);
  if (publicData?.publicUrl) {
    return { url: publicData.publicUrl, path: uploadedPath };
  }

  throw new Error(getErrorMessage(lastSignError, 'Upload completed but failed to create an accessible file URL'));
}

export async function getFreshVideoUrl(
  storagePath: string | null,
  fallbackUrl: string | null
): Promise<string | null> {
  if (storagePath) {
    const { data, error } = await supabase.storage
      .from(STORAGE_BUCKET)
      .createSignedUrl(storagePath, 60 * 60 * 24 * 7);
    if (!error && data?.signedUrl) return data.signedUrl;
  }
  if (fallbackUrl) {
    const path = extractStoragePath(fallbackUrl);
    if (path) {
      const { data, error } = await supabase.storage
        .from(STORAGE_BUCKET)
        .createSignedUrl(path, 60 * 60 * 24 * 7);
      if (!error && data?.signedUrl) return data.signedUrl;
    }
  }
  return fallbackUrl;
}

export async function fetchHyperDuskProjects(): Promise<
  (HyperDuskProject & { image_count: number; thumbnail_url: string | null })[]
> {
  const { data } = await supabase
    .from('imagemotion_projects')
    .select('*, imagemotion_project_images(image_url, image_path, dusk_image_url, dusk_image_path, order_index)')
    .eq('service_type', 'hyper_dusk')
    .order('created_at', { ascending: false });

  const projects = (data ?? []).map((p: any) => {
    const images = p.imagemotion_project_images ?? [];
    const sorted = [...images].sort((a: any, b: any) => a.order_index - b.order_index);
    const first = sorted[0];
    // Prefer the generated night still as the thumbnail so completed
    // image-only projects (which have no video) show their deliverable.
    return {
      ...p,
      image_count: images.length,
      thumbnail_url: first?.dusk_image_url ?? first?.image_url ?? null,
      _thumbnail_path: first?.dusk_image_path ?? first?.image_path ?? null,
    };
  });

  const refreshed = await Promise.all(
    projects.map(async (p: any) => {
      if (p.thumbnail_url) {
        const fresh = await getFreshVideoUrl(p._thumbnail_path, p.thumbnail_url);
        if (fresh) p.thumbnail_url = fresh;
      }
      delete p._thumbnail_path;
      return p;
    })
  );

  return refreshed as (HyperDuskProject & { image_count: number; thumbnail_url: string | null })[];
}

export async function fetchProject(
  projectId: string
): Promise<{ project: HyperDuskProject | null; images: HyperDuskProjectImage[] }> {
  const { data, error } = await supabase
    .from('imagemotion_projects')
    .select('*')
    .eq('id', projectId)
    .eq('service_type', 'hyper_dusk')
    .single();

  if (error || !data) return { project: null, images: [] };

  const { data: imgData } = await supabase
    .from('imagemotion_project_images')
    .select('*')
    .eq('project_id', projectId)
    .order('order_index', { ascending: true });

  const project = data as HyperDuskProject;

  if (project.status === 'completed' && (project.final_video_path || project.final_video_url)) {
    const freshUrl = await getFreshVideoUrl(project.final_video_path, project.final_video_url);
    if (freshUrl) project.final_video_url = freshUrl;
  }

  const images = (imgData ?? []) as HyperDuskProjectImage[];
  await Promise.all(
    images.map(async (img) => {
      const fresh = await getFreshVideoUrl(img.image_path, img.image_url);
      if (fresh) img.image_url = fresh;
      // The original upload is kept in storage permanently, but its signed
      // URL expires (7 days). Re-sign it from the stored path — otherwise the
      // "Original" panel renders a broken image on older projects even though
      // the file is still there. Fall back to the (freshly re-signed)
      // image_url path so an original is always recoverable.
      if (img.source_image_path || img.source_image_url) {
        const freshSource = await getFreshVideoUrl(
          img.source_image_path ?? img.image_path ?? null,
          img.source_image_url ?? img.image_url ?? null
        );
        if (freshSource) img.source_image_url = freshSource;
      } else if (img.image_url) {
        // Legacy rows created without a dedicated source column: surface the
        // re-signed primary image as the original so it still displays.
        img.source_image_url = img.image_url;
      }
      if (img.dusk_image_path || img.dusk_image_url) {
        const freshDusk = await getFreshVideoUrl(img.dusk_image_path ?? null, img.dusk_image_url ?? null);
        if (freshDusk) img.dusk_image_url = freshDusk;
      }
      const variants = normalizeDuskVariants((img as any).dusk_variants);
      if (variants.length) {
        const refreshedVariants = await Promise.all(
          variants.map(async (variant) => {
            const variantUrl = await getFreshVideoUrl(variant.path, variant.url ?? null);
            return { ...variant, url: variantUrl ?? variant.url ?? null };
          })
        );
        img.dusk_variants = refreshedVariants;
      } else {
        img.dusk_variants = [];
      }
    })
  );

  return { project, images };
}

export async function fetchUserCredits(userId: string): Promise<number> {
  const { data, error } = await supabase
    .from('profiles')
    .select('credits')
    .eq('id', userId)
    .single();
  if (error || !data) return 0;
  return data.credits ?? 0;
}

export async function consumeCreditsForProject(
  userId: string,
  projectId: string,
  credits: number
): Promise<{ success: boolean; error?: string; credits_remaining?: number }> {
  const { data, error } = await supabase.rpc('consume_credits_for_imagemotion', {
    p_user_id: userId,
    p_project_id: projectId,
    p_credits: credits,
  });

  if (error) {
    if (error.message.includes('INSUFFICIENT_CREDITS')) {
      return { success: false, error: 'INSUFFICIENT_CREDITS' };
    }
    return { success: false, error: error.message };
  }
  return data as { success: boolean; error?: string; credits_remaining?: number };
}

export async function deleteProject(projectId: string): Promise<void> {
  const { error: imgError } = await supabase
    .from('imagemotion_project_images')
    .delete()
    .eq('project_id', projectId);

  if (imgError) throw imgError;

  const { error: projError } = await supabase
    .from('imagemotion_projects')
    .delete()
    .eq('id', projectId)
    .eq('service_type', 'hyper_dusk');

  if (projError) throw projError;
}

/**
 * Mark an image-only Hyper Dusk project as completed. There is no Kling
 * video for these projects — the deliverable is the approved dusk still —
 * so we flip the project to `completed` (without a final_video_url) and the
 * image row to `completed`. Permitted client-side by the existing
 * "Users can update own imagemotion projects" RLS policy.
 */
export async function completeImageOnlyProject(
  projectId: string,
  imageId?: string
): Promise<void> {
  const { error: projError } = await supabase
    .from('imagemotion_projects')
    .update({ status: 'completed', error_message: null, updated_at: new Date().toISOString() })
    .eq('id', projectId)
    .eq('service_type', 'hyper_dusk');

  if (projError) throw new Error(getErrorMessage(projError, 'Failed to finalize image project'));

  if (imageId) {
    await supabase
      .from('imagemotion_project_images')
      .update({ generation_status: 'completed', generation_error: null })
      .eq('id', imageId)
      .eq('project_id', projectId);
  }
}

/**
 * Re-open a finished image-only project for the video flow. Flips
 * `settings.output_mode` to `video` (merging into existing settings) and
 * resets the project to `editing` so the animation controls reappear.
 * The caller charges the upgrade top-up and then approves the chosen dusk
 * variant (via `approveHyperDuskPreview`) to put the image back into
 * `dusk_ready` ready for Kling.
 */
export async function upgradeImageProjectToVideo(projectId: string): Promise<void> {
  const { data: current, error: fetchError } = await supabase
    .from('imagemotion_projects')
    .select('settings')
    .eq('id', projectId)
    .eq('service_type', 'hyper_dusk')
    .single();

  if (fetchError) throw new Error(getErrorMessage(fetchError, 'Failed to load project for upgrade'));

  const mergedSettings = {
    ...((current?.settings as Record<string, unknown> | null) ?? {}),
    output_mode: 'video',
  };

  const { error: updateError } = await supabase
    .from('imagemotion_projects')
    .update({
      settings: mergedSettings,
      status: 'editing',
      final_video_url: null,
      final_video_path: null,
      error_message: null,
      updated_at: new Date().toISOString(),
    })
    .eq('id', projectId)
    .eq('service_type', 'hyper_dusk');

  if (updateError) throw new Error(getErrorMessage(updateError, 'Failed to upgrade project to video'));
}

export async function createProject(params: {
  userId: string;
  name: string;
  images: { url: string; path: string | null; orderIndex: number }[];
  nanoBananaPrompt?: string | null;
  klingMotionPrompt?: string | null;
  aspectRatio?: string;
  outputMode?: HyperDuskOutputMode;
}): Promise<string> {
  const ar = params.aspectRatio || '16:9';
  const { data: project, error: projectError } = await supabase
    .from('imagemotion_projects')
    .insert({
      user_id: params.userId,
      name: params.name || null,
      status: 'editing',
      quality: 'standard',
      service_type: 'hyper_dusk',
      transition_type: 'dissolve',
      settings: {
        transition_effect: 'dissolve',
        aspect_ratio: ar,
        output_resolution: 'hd',
        clip_duration: 5,
        output_mode: params.outputMode ?? 'video',
      },
      music_url: null,
      logo_url: null,
    })
    .select()
    .single();

  if (projectError) throw new Error(getErrorMessage(projectError, 'Failed to create project'));

  const imageRows = params.images.map((img) => ({
    project_id: project.id,
    image_url: img.url,
    image_path: img.path,
    source_image_url: img.url,
    source_image_path: img.path,
    order_index: img.orderIndex,
    clip_duration: 5,
    motion_prompt: params.klingMotionPrompt ?? DEFAULT_HYPER_DUSK_MOTION_PROMPT,
    nano_banana_prompt: params.nanoBananaPrompt ?? DEFAULT_HYPER_DUSK_NANO_PROMPT,
    kling_motion_prompt: params.klingMotionPrompt ?? DEFAULT_HYPER_DUSK_MOTION_PROMPT,
    generation_status: 'pending',
  }));

  const { error: imagesError } = await supabase
    .from('imagemotion_project_images')
    .insert(imageRows);

  if (imagesError) throw new Error(getErrorMessage(imagesError, 'Failed to create project images'));
  return project.id;
}

export async function createHyperDuskProject(params: {
  userId: string;
  name: string;
  image: { url: string; path: string | null };
  nanoPrompt?: string;
  klingPromptBase?: string;
  animationStyle?: HyperDuskAnimationStyle;
  animationDurationSeconds?: number;
  aspectRatio?: string;
  outputMode?: HyperDuskOutputMode;
}): Promise<string> {
  const duration = Math.max(5, Math.min(10, Math.round(params.animationDurationSeconds ?? 5)));
  return createProject({
    userId: params.userId,
    name: params.name,
    nanoBananaPrompt: params.nanoPrompt ?? DEFAULT_HYPER_DUSK_NANO_PROMPT,
    klingMotionPrompt: params.klingPromptBase ?? DEFAULT_HYPER_DUSK_MOTION_PROMPT,
    images: [{ ...params.image, orderIndex: 0 }],
    aspectRatio: params.aspectRatio,
    outputMode: params.outputMode,
  }).then(async (createdProjectId) => {
    const { error } = await supabase
      .from('imagemotion_project_images')
      .update({
        clip_duration: duration,
        kling_animation_style: params.animationStyle ?? DEFAULT_HYPER_DUSK_ANIMATION_STYLE,
      })
      .eq('project_id', createdProjectId);
    if (error) throw error;
    return createdProjectId;
  });
}

export async function generateHyperDuskPreview(
  projectId: string,
  options?: {
    presetId?: string;
    nanoPrompt?: string;
    klingPromptBase?: string;
    animationStyle?: HyperDuskAnimationStyle;
    sceneType?: string;
  }
): Promise<void> {
  const { data: sessionData } = await supabase.auth.getSession();
  const accessToken = sessionData.session?.access_token;
  if (!accessToken) throw new Error('You must be logged in to generate Hyper Dusk preview');

  const { data, error } = await supabase.functions.invoke('imagemotion-hyperdusk-generate', {
    body: { projectId, phase: 'dusk_preview', ...options },
    headers: { Authorization: `Bearer ${accessToken}` },
  });

  if (error) {
    const msg = await getFunctionInvokeErrorMessage(error, 'Dusk preview generation failed');
    throw new Error(msg);
  }

  if (data && typeof data === 'object' && 'error' in data && data.error) {
    throw new Error(String(data.error));
  }
}

/**
 * Persist an edited night still as a dusk variant via the Hyper Dusk edge
 * function (service role). Direct client UPDATEs on imagemotion_project_images
 * are blocked by RLS, so polish / PhotoEdit returns must go through here.
 */
async function invokeSaveEditedVariant(body: {
  projectId: string;
  imageId: string;
  path?: string;
  url?: string;
  imageUrl?: string;
}): Promise<{ variantId: string }> {
  const { data: sessionData } = await supabase.auth.getSession();
  const accessToken = sessionData.session?.access_token;
  if (!accessToken) throw new Error('You must be logged in to save an edited variant');

  const { data, error } = await supabase.functions.invoke('imagemotion-hyperdusk-generate', {
    body: { phase: 'save_edited_variant', ...body },
    headers: { Authorization: `Bearer ${accessToken}` },
  });

  if (error) {
    throw new Error(await getFunctionInvokeErrorMessage(error, 'Failed to save edited variant'));
  }
  if (data && typeof data === 'object' && 'error' in data && data.error) {
    throw new Error(String(data.error));
  }
  const variantId =
    data && typeof data === 'object' && typeof (data as { variantId?: unknown }).variantId === 'string'
      ? (data as { variantId: string }).variantId
      : null;
  if (!variantId) throw new Error('Edited variant saved but no variant id was returned');
  return { variantId };
}

/** Upload a polished still, then append it as the selected dusk variant. */
export async function saveEditedDuskVariant(params: {
  projectId: string;
  imageId: string;
  userId: string;
  file: File;
}): Promise<{ variantId: string }> {
  const uploaded = await uploadToStorage(params.file, 'images', params.userId);
  return invokeSaveEditedVariant({
    projectId: params.projectId,
    imageId: params.imageId,
    path: uploaded.path,
    url: uploaded.url,
  });
}

/**
 * Persist a PhotoEdit (or other app) return URL as a new dusk variant.
 * The edge function fetches the URL server-side (avoids browser CORS).
 */
export async function saveEditedDuskVariantFromUrl(params: {
  projectId: string;
  imageId: string;
  userId: string;
  imageUrl: string;
}): Promise<{ variantId: string }> {
  return invokeSaveEditedVariant({
    projectId: params.projectId,
    imageId: params.imageId,
    imageUrl: params.imageUrl,
  });
}

export async function approveHyperDuskPreview(
  projectId: string,
  imageId?: string,
  selectedVariantId?: string
): Promise<void> {
  const { data: sessionData } = await supabase.auth.getSession();
  const accessToken = sessionData.session?.access_token;
  if (!accessToken) throw new Error('You must be logged in to approve');

  const { data, error } = await supabase.functions.invoke('imagemotion-hyperdusk-generate', {
    body: { projectId, phase: 'approve', imageId, selectedVariantId },
    headers: { Authorization: `Bearer ${accessToken}` },
  });

  if (error) {
    const msg = await getFunctionInvokeErrorMessage(error, 'Failed to approve dusk preview');
    throw new Error(msg);
  }

  if (data && typeof data === 'object' && 'error' in data && data.error) {
    throw new Error(String(data.error));
  }
}

export async function generateHyperDuskAnimation(
  projectId: string,
  options?: {
    durationSeconds?: number;
    animationStyle?: HyperDuskAnimationStyle;
    selectedVariantId?: string;
  }
): Promise<void> {
  const { data: sessionData } = await supabase.auth.getSession();
  const accessToken = sessionData.session?.access_token;
  if (!accessToken) throw new Error('You must be logged in to generate Hyper Dusk animation');

  const { data: readinessRows, error: readinessError } = await supabase
    .from('imagemotion_project_images')
    .select('id, generation_status')
    .eq('project_id', projectId);

  if (readinessError) throw new Error(getErrorMessage(readinessError, 'Failed to validate Hyper Dusk approval state'));
  const hasApprovedVariant = (readinessRows ?? []).some((row: any) => row.generation_status === 'dusk_ready');
  if (!hasApprovedVariant) throw new Error('Please approve a dusk image first, then generate the video.');

  const { error } = await supabase.functions.invoke('imagemotion-hyperdusk-generate', {
    body: { projectId, phase: 'animation', ...options },
    headers: { Authorization: `Bearer ${accessToken}` },
  });

  if (error) throw new Error(await getFunctionInvokeErrorMessage(error, 'Failed to generate Hyper Dusk animation'));
}

export async function pollHyperDuskStatus(
  projectId: string
): Promise<{ results?: Array<{ imageId: string; status: string; done: boolean; error?: string }>; error?: string }> {
  const { data: sessionData } = await supabase.auth.getSession();
  const accessToken = sessionData.session?.access_token;
  if (!accessToken) return { error: 'Not logged in' };

  const { data, error } = await supabase.functions.invoke('imagemotion-hyperdusk-poll', {
    body: { projectId },
    headers: { Authorization: `Bearer ${accessToken}` },
  });

  if (error) {
    const msg = await getFunctionInvokeErrorMessage(error, 'Poll failed');
    return { error: msg };
  }

  return data as { results?: Array<{ imageId: string; status: string; done: boolean; error?: string }> };
}
